import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminPageHeader, StatusPill, AdminEmpty } from "@/components/admin/AdminBits";
import { RichTextEditor } from "@/components/RichTextEditor";
import { MediaUploader } from "@/components/MediaUploader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { title: "", date: today(), content: "", status: "draft", media: [] };

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/news"); setItems(data); } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (n) => { setForm({ title: n.title, date: (n.date || "").slice(0, 10), content: n.content || "", status: n.status, media: n.media || [] }); setEditId(n.id); setOpen(true); };

  const save = async (status) => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const payload = { ...form, status };
    try {
      if (editId) await api.put(`/admin/news/${editId}`, payload);
      else await api.post("/admin/news", payload);
      toast.success(status === "published" ? "Published" : "Saved as draft");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const togglePublish = async (n) => {
    try {
      await api.put(`/admin/news/${n.id}`, { ...n, date: (n.date || "").slice(0, 10), status: n.status === "published" ? "draft" : "published" });
      toast.success(n.status === "published" ? "Unpublished" : "Published");
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const del = async () => {
    try { await api.delete(`/admin/news/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-news-page">
      <AdminPageHeader title="News" subtitle="Manage official announcements."
        action={<button onClick={openNew} data-testid="news-add-button" className="inline-flex items-center gap-2 rounded-full bg-ukf-red px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-ukf-red-dark active:scale-[0.98]"><Plus className="h-4 w-4" /> New post</button>} />

      {loading ? <p className="text-sm text-slate-400">Loading…</p> : items.length === 0 ? (
        <AdminEmpty title="No news yet" message="Create your first announcement." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-5 py-3">Title</th><th className="px-5 py-3">Date</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((n) => (
                <tr key={n.id} data-testid={`news-row-${n.id}`} className="hover:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-ukf-ink">
                    <span className="flex items-center gap-2">{n.title}{n.media?.length > 0 && <Paperclip className="h-3.5 w-3.5 text-slate-400" />}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-500">{(n.date || "").slice(0, 10)}</td>
                  <td className="px-5 py-3"><StatusPill status={n.status} /></td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => togglePublish(n)} data-testid={`news-toggle-${n.id}`} title={n.status === "published" ? "Unpublish" : "Publish"} className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-ukf-red">{n.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                      <button onClick={() => openEdit(n)} data-testid={`news-edit-${n.id}`} className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-ukf-red"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setDelId(n.id)} data-testid={`news-delete-${n.id}`} className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-red-50 hover:text-ukf-red"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="news-editor-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit news" : "New news post"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title-input" className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="news-date-input" className="rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Content</label>
              <RichTextEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Media &amp; documents</label>
              <MediaUploader media={form.media} onChange={(m) => setForm({ ...form, media: m })} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => save("draft")} disabled={saving} data-testid="news-save-draft-button" className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Save draft</button>
            <button onClick={() => save("published")} disabled={saving} data-testid="news-publish-button" className="rounded-full bg-ukf-red px-5 py-2.5 text-sm font-semibold text-white hover:bg-ukf-red-dark disabled:opacity-60">{editId ? "Update &amp; publish" : "Publish"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this post?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="news-confirm-delete" className="bg-ukf-red hover:bg-ukf-red-dark">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
